import {
  Drawer,
  Box,
  IconButton,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Close,
  LibraryAddCheck,
  Edit,
  Today,
  AccountCircle,
  ExpandMore,
} from '@mui/icons-material';
import { IAssessmentsTableEntry } from 'views/pages/Patient/components/Assessments/components/AssessmentsTable/hooks';
import { t } from 'i18next';
import dayjs from 'dayjs';
import { AssessmentDrawerItem } from 'views/pages/Patient/components/Assessments/components/AssessmentDrawer/AssessmentDrawerItem.tsx';

interface IAssessmentDrawerProps {
  open: boolean;
  onClose: () => void;
  details: IAssessmentsTableEntry;
  updateAction: (id: string) => void;
  downloadPdfAction: (patient_id: string, id: string) => void;
  patient_id: string;
  isDownloading: boolean;
  disabled: boolean;
}

export const AssessmentDrawer = ({
  open,
  onClose,
  details,
  updateAction,
  downloadPdfAction,
  patient_id,
  isDownloading,
  disabled,
}: IAssessmentDrawerProps) => {
  const {
    name,
    id,
    completed,
    date,
    leadingAnswers,
    answerGroups,
    assessingProvider,
  } = details;

  return (
    <Drawer anchor='right' open={open} onClose={onClose}>
      <Box
        sx={{
          width: 600,
          height: '100%',
          p: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            paddingY: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2 }}>
            <LibraryAddCheck sx={{ width: 46, height: 46 }} color='primary' />
            <Box>
              <Typography variant='h6'>{name}</Typography>
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                {t('patient.assessments.assessmentDrawer.subTitle')}
              </Typography>
            </Box>
          </Box>

          <IconButton onClick={onClose} size='small'>
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 2 }}>
          <Button
            variant='outlined'
            sx={{ flex: 1 }}
            startIcon={<Edit />}
            onClick={() => updateAction(id)}
            disabled={disabled}
          >
            {t('patient.assessments.assessmentDrawer.update')}
          </Button>
          <Button
            variant='contained'
            color='secondary'
            sx={{ flex: 1 }}
            onClick={() => downloadPdfAction(patient_id, id)}
            disabled={isDownloading || disabled || !completed}
          >
            {t('patient.assessments.assessmentDrawer.downloadPdf')}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Today
              sx={{ width: 16, height: 16, color: 'rgba(0, 0, 0, 0.56)' }}
            />
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              {t('patient.assessments.assessmentDrawer.assessmentDate')}
            </Typography>
            <Typography variant='body2'>
              {dayjs(date).format('MMM DD, YYYY')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <AccountCircle
              sx={{ width: 16, height: 16, color: 'rgba(0, 0, 0, 0.56)' }}
            />
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              {t('patient.assessments.assessmentDrawer.assessmentProvider')}
            </Typography>
            <Typography variant='body2'>
              {assessingProvider
                ? `${assessingProvider.name} ${assessingProvider.last_name || ''} ${assessingProvider.suffix || ''}`
                : '-'}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            height: '1px',
            bgcolor: theme => theme.palette.grey[300],
            marginY: 3,
          }}
        />

        <Box
          sx={{
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 300px)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box>
            {leadingAnswers.map(({ answer, question }, idx) => {
              return (
                <AssessmentDrawerItem
                  answer={answer}
                  question={question}
                  key={idx}
                />
              );
            })}
          </Box>
          <Box>
            {answerGroups.map((item, idx) => {
              return (
                <Accordion
                  key={idx}
                  defaultExpanded={idx === 0}
                  sx={{
                    width: '100%',
                    '&::before': {
                      display: 'none',
                    },
                  }}
                  elevation={0}
                  disableGutters
                >
                  <AccordionSummary expandIcon={<ExpandMore />} sx={{ p: 0 }}>
                    <Typography variant='subtitle2'>
                      {item.groupName}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    {item.answers.map(({ answer, question }, idx) => {
                      return (
                        <AssessmentDrawerItem
                          answer={answer}
                          question={question}
                          key={idx}
                        />
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};
